/**
 * Module review counts per programme whose module reviews the reader decides.
 *
 * Pure, so the counting is testable without rendering a table.
 *
 * @module components/overview/module-review-rows
 */

import type { AssignedModuleSummary, ProgrammeSummary, SessionContext } from "@/platform";

import type { ModuleReviewRow } from "./ModuleReviewTable";

/**
 * @param session - Whose responsibilities decide which programmes count.
 * @param programmes - Every programme the account can reach.
 * @param modules - Every module it can reach, across those programmes.
 * @returns One row per programme the account leads, or every one for a client
 *   admin. Empty for anyone else.
 */
export function moduleReviewRows(
  session: SessionContext,
  programmes: readonly ProgrammeSummary[],
  modules: readonly AssignedModuleSummary[],
): ModuleReviewRow[] {
  const led = new Set(
    session.subject.responsibilities
      .filter((assignment) => assignment.responsibility === "lead")
      .map((assignment) => assignment.programmeId),
  );

  // A client admin decides module reviews anywhere, as the attention queue asks.
  const administers = session.subject.accountRole === "client-admin";

  return programmes
    .filter((programme) => administers || led.has(programme.id))
    .map((programme) => {
      const own = modules.filter((module) => module.programmeId === programme.id);
      const count = (status: AssignedModuleSummary["moduleReviewStatus"]): number =>
        own.filter((module) => module.moduleReviewStatus === status).length;
      return {
        programmeId: programme.id,
        title: programme.title,
        moduleCount: programme.moduleCount,
        draft: count("Draft"),
        submitted: count("Submitted for Review"),
        changes: count("Changes Requested"),
        approved: count("Approved"),
        // Modules with no builder on them, which is the number a lead is
        // most likely looking for. Not `moduleCount - own.length`: a lead is
        // handed every module of a programme whether they own it or not, so
        // that subtraction is always zero.
        unassigned: own.filter((module) => !module.ownerUserId).length,
      };
    });
}
